import { useCallback, useEffect, useRef, useState } from 'react';
import { getProjectServices } from '../services/SandboxService';
import { projectStartKey, runStart } from '../components/sandbox/startRuns';
import { startProject, stopProject } from '../components/sandbox/projectRun';
import { reportText, runPlan } from '../utils/workspacePageModel';

/**
 * Start and Stop on many projects of a workspace (its header's Start all / Stop all, the Projects table's selection).
 * `projectRun` is `useProjectsRunState`'s answer (the states already loaded are not asked again).
 * - `prepare(action, projects)` resolves to the plan (utils/workspacePageModel.js runPlan): the projects the person
 *   may run and those skipped with their reason, so the page can ask first;
 * - `execute(action, plan)` does it (Start goes on in every project at once and follows each until it runs, Stop one
 *   project after the other) and resolves to the report text, also kept in `report` until `clearReport()`;
 * - `busy` is 'start' | 'stop' | null while it goes on;
 * - `setReport({ text, tone })` lets the page report what another bulk action did, in the same place.
 */
export default function useWorkspaceRun(projectRun) {
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const prepare = useCallback(async(action, projects) => {
    const answers = {};
    await Promise.all(projects.map(async(project) => {
      answers[project.projectId] = projectRun.states[project.projectId] !== undefined
        ? projectRun.states[project.projectId]
        : await getProjectServices(project.projectId).catch(() => null);
    }));
    return runPlan(action, projects, answers);
  }, [projectRun.states]);

  const execute = useCallback(async(action, plan) => {
    setBusy(action);
    setReport(null);
    const done = [];
    const failed = [];
    try {
      if (action === 'start') {
        await Promise.all(plan.eligible.map(async(entry) => {
          const result = await runStart(projectStartKey(entry.projectId), (onProgress) => startProject(entry.projectId, entry.items, {
            onEach: () => projectRun.refresh(entry.projectId),
            onProgress,
          }));
          projectRun.refresh(entry.projectId);
          if (result?.progress?.error) { failed.push({ name: entry.name, error: result.progress.error }); } else { done.push(entry.name); }
        }));
      } else {
        for (const entry of plan.eligible) {
          // eslint-disable-next-line no-await-in-loop
          const { error } = await stopProject(entry.projectId, entry.items);
          projectRun.refresh(entry.projectId);
          if (error) { failed.push({ name: entry.name, error }); } else { done.push(entry.name); }
        }
      }
    } finally {
      if (alive.current) { setBusy(null); }
    }
    const text = reportText({ verb: action === 'start' ? 'Started' : 'Stopped', done, skipped: plan.skipped, failed });
    const next = { text, tone: failed.length ? 'error' : 'ok' };
    if (alive.current) { setReport(next); }
    return next;
  }, [projectRun]);

  return { prepare, execute, busy, report, setReport, clearReport: () => setReport(null) };
}
